import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import {
  Bell,
  Check,
  CheckCheck,
  Settings,
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  Workflow,
  Info,
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  is_archived: boolean;
  action_url: string | null;
  action_label: string | null;
  created_at: string;
  read_at: string | null;
}

interface NotificationsCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationsCenter({ isOpen, onClose }: NotificationsCenterProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('notifications')
        .select('*')
        .eq('is_archived', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (filter === 'unread') {
        query = query.eq('is_read', false);
      }

      const { data, error } = await query;

      if (error) throw error;

      setNotifications(data || []);
    } catch (error: any) {
      console.error('Error loading notifications:', error);
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .rpc('mark_notification_read', { p_notification_id: notificationId });

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      toast.error('Erro ao marcar notificação');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read');

      if (error) throw error;

      toast.success('Todas as notificações foram marcadas como lidas');
      await loadNotifications();
    } catch (error: any) {
      console.error('Error marking all as read:', error);
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'contract_expiring':
        return <FileText className="w-5 h-5 text-secondary-foreground" />;
      case 'approval_needed':
        return <AlertCircle className="w-5 h-5 text-destructive" />;
      case 'obligation_due':
        return <Clock className="w-5 h-5 text-secondary-foreground" />;
      case 'workflow_updated':
        return <Workflow className="w-5 h-5 text-accent" />;
      default:
        return <Info className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'warning';
      case 'low':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-end">
      <div className="bg-card w-full max-w-md h-full shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-accent text-white p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6" />
              <h2 className="text-xl font-bold">Notificações</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-muted-foreground transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-white text-accent'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              Não lidas
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-white text-accent'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              Todas
            </button>
          </div>
        </div>

        {/* Actions bar */}
        {notifications.length > 0 && (
          <div className="px-4 py-3 border-b border-border flex justify-between items-center bg-muted">
            <span className="text-sm text-muted-foreground">
              {notifications.filter(n => !n.is_read).length} não lidas
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={notifications.filter(n => !n.is_read).length === 0}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Marcar todas como lidas
            </Button>
          </div>
        )}

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma notificação</p>
              <p className="text-sm">
                {filter === 'unread'
                  ? 'Você está em dia! Não há notificações não lidas.'
                  : 'Não há notificações para mostrar.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 transition-colors ${
                    !notification.is_read
                      ? 'bg-accent/10/20'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-medium text-foreground">
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-accent hover:text-accent/80"
                            title="Marcar como lida"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground mb-2">
                        {notification.message}
                      </p>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </span>

                        {notification.priority !== 'normal' && (
                          <Badge variant={getPriorityColor(notification.priority) as any} size="sm">
                            {notification.priority === 'urgent'
                              ? 'Urgente'
                              : notification.priority === 'high'
                              ? 'Alta'
                              : 'Baixa'}
                          </Badge>
                        )}

                        {notification.action_url && (
                          <Link
                            to={notification.action_url}
                            onClick={onClose}
                            className="text-xs text-accent hover:underline"
                          >
                            {notification.action_label || 'Ver detalhes'} →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 bg-muted">
          <Link to="/notifications/settings" onClick={onClose}>
            <Button variant="outline" className="w-full">
              <Settings className="w-4 h-4 mr-2" />
              Configurações de Notificações
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
